import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { countries as countryList } from "countries-list";
import {
  copyToClipboard,
  debounce,
  getDateFromTimestamp,
  sortObjectByNumber,
  sortObjectByString,
} from "../../../utils/helper";
import { Popover, Tooltip } from "antd";
import {
  EditOutlined,
  FileTextOutlined,
  LinkOutlined,
  StarFilled,
} from "@ant-design/icons";
import dayjs from "dayjs";
import useGroupsData from "./useGroupsData";
import { AnnouncementIcon } from "../../../utils/icons";

const useGroups = () => {
  const navigate = useNavigate();
  const { groupsList, isLoading, refreshGroups } = useGroupsData();

  const [clubs, setClubs] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [countries, setCountries] = useState([]);
  const [openNotesModal, setOpenNotesModal] = useState(false);
  const [selectedGroupDetails, setSelectedGroupDetails] = useState(null);
  const [filteredGroupsList, setFilteredGroupsList] = useState([]);
  const [membersCount, setMembersCount] = useState({
    totalReg: 0,
    totalPending: 0,
    totalMembers: 0,
  });
  const [filters, setFilters] = useState({
    searchTerm: "",
    createdOn: null,
    clubId: null,
    leagueId: null,
    country: null,
    status: null,
    adoption: null,
    owner: null,
  });

  const generateDropdownData = (groups) => {
    const clubSet = new Map();
    const leagueSet = new Map();
    const countrySet = new Map();

    groups.forEach((group) => {
      if (group.clubId && group.clubName) {
        clubSet.set(group.clubId?.toString(), group.clubName);
      }
      if (group.leagueId && group.leagueName) {
        leagueSet.set(group.leagueId?.toString(), group.leagueName);
      }
      if (group.supportersCountry) {
        countrySet.set(
          group.supportersCountry?.toString(),
          group.supportersCountry,
        );
      }
    });

    // First create the arrays
    const clubsArray = Array.from(clubSet.entries())
      .filter(([key, value]) => key && value)
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([key, value]) => ({
        value: key.toString(),
        label: <span>{value}</span>,
      }));

    const leaguesArray = Array.from(leagueSet.entries())
      .filter(([key, value]) => key && value)
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([key, value]) => ({
        value: key.toString(),
        label: <span>{value}</span>,
      }));

    const countriesArray = Array.from(countrySet.entries())
      .filter(([key, value]) => key && value)
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([key, value]) => {
        if (value === "World") {
          return {
            value: "World",
            label: <span>{"World"}</span>,
          };
        }
        return {
          value: key.toString(),
          label: <span>{countryList?.[value]?.name || value}</span>,
        };
      });

    // Then set all states at once
    setClubs(clubsArray);
    setLeagues(leaguesArray);
    setCountries(countriesArray);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleDropdownChange = (type, selectedValue) => {
    handleFilterChange(type, selectedValue);
  };

  const handleSearchInputChange = (value) => {
    handleFilterChange("searchTerm", value);
  };

  const handleDatePickerChange = (value) => {
    handleFilterChange("createdOn", value);
  };

  const handleRefresh = () => {
    refreshGroups();
  };

  const handleGroupNoteClick = (groupData) => {
    setOpenNotesModal(true);
    setSelectedGroupDetails(groupData);
  };

  const applyFilter = useCallback(
    (groupsList) => {
      let filteredGroupsList = [];

      filteredGroupsList = groupsList.filter((group) => {
        const matchesSearch =
          !filters.searchTerm ||
          group?.groupName
            ?.toLowerCase()
            .includes(filters.searchTerm.toLowerCase());

        const matchesClub = !filters.clubId || group.clubId === filters.clubId;

        const matchesLeague =
          !filters.leagueId || group.leagueId === filters.leagueId;

        const matchesCountry =
          !filters.country || group.supportersCountry === filters.country;

        const matchesStatus =
          !filters.status || group.status === filters.status;

        let matchesAdoption = false;

        if (filters.adoption === "Channels") {
          if (group?.showChatChannels) {
            matchesAdoption = true;
          }
        } else if (filters.adoption === "Stripe") {
          if (group?.paymentDetails && group?.paymentDetails?.stripeAccountId) {
            matchesAdoption = true;
          }
        } else if (filters.adoption === "Website") {
          if (group?.websitePublished) {
            matchesAdoption = true;
          }
        } else if (filters.adoption === "Directory") {
          if (group?.directoryPublished) {
            matchesAdoption = true;
          }
        } else if (filters.adoption === "Stadium") {
          if (group?.stadiumPublished) {
            matchesAdoption = true;
          }
        } else if (filters.adoption === "Global") {
          if (group?.clubPostAllowed) {
            matchesAdoption = true;
          }
        } else {
          matchesAdoption = !filters.adoption;
        }

        const matchesOwner = !filters.owner || group.owner === filters.owner;

        const matchesCreatedAfter =
          !filters.createdOn || group.createdOn > filters.createdOn;

        return (
          matchesSearch &&
          matchesClub &&
          matchesLeague &&
          matchesCountry &&
          matchesStatus &&
          matchesAdoption &&
          matchesOwner &&
          matchesCreatedAfter
        );
      });

      const debouncedCalculate = debounce(() => {
        calculateMembersCount(filteredGroupsList);
      }, 300);

      debouncedCalculate();

      return filteredGroupsList;
    },
    [filters],
  );

  const calculateMembersCount = (groups) => {
    const count = groups.reduce(
      (acc, curr) => {
        acc.totalReg += curr.registeredMembers;
        acc.totalPending += curr.preRegisteredMemberCount;
        acc.totalMembers += curr.totalMembers;
        return acc;
      },
      { totalReg: 0, totalPending: 0, totalMembers: 0 },
    );

    setMembersCount(count);
  };

  useEffect(() => {
    if (groupsList?.length > 0) {
      const filteredData = applyFilter(groupsList);
      setFilteredGroupsList(filteredData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupsList, filters]);

  useEffect(() => {
    if (groupsList?.length > 0) {
      generateDropdownData(groupsList);
      calculateMembersCount(groupsList);
      // setFilteredGroupsList(groupsList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupsList]);

  const columns = [
    {
      title: "Actions",
      width: 100,
      render: (data) => (
        <div className="flex-center" style={{ gap: "8px" }}>
          <Tooltip title="edit">
            <EditOutlined
              style={{ fontSize: 18 }}
              onClick={() => {
                navigate(`/superadmin/groups/edit/${data.id}`);
              }}
            />
          </Tooltip>
          <Tooltip title="View Notes">
            <FileTextOutlined
              style={{ fontSize: 18 }}
              onClick={() => {
                handleGroupNoteClick(data);
              }}
            />
          </Tooltip>
        </div>
      ),
    },
    {
      width: 250,
      title: "Group",
      key: "groupName",
      dataIndex: "groupName",
      filters: [
        {
          text: "Priority Group",
          value: "Priority Group",
        },
        {
          text: "Social Announcement",
          value: "Social Announcement",
        },
      ],
      sorter: (a, b) => sortObjectByString(a, b, "groupName"),
      onFilter: (value, record) => {
        if (value === "Priority Group") {
          return record.priorityGroup;
        }

        if (value === "Social Announcement") {
          return record.socialAnnouncement;
        }
      },
      render: (groupName, data) => {
        return (
          <div style={{ display: "flex", gap: "24px" }}>
            <div className="flex-center">
              <span style={{ fontWeight: "bold" }}>{groupName}</span>
              <span
                style={{
                  marginLeft: 4,
                  cursor: "pointer",
                }}
              >
                <Tooltip
                  title="Copy Group ID"
                  onClick={() => copyToClipboard(data.id)}
                >
                  (<span style={{ textDecoration: "underline" }}>Id</span>)
                </Tooltip>
              </span>
            </div>

            <div className="flex-center" style={{ gap: "8px" }}>
              {data.priorityGroup && (
                <Tooltip title="Priority Group">
                  <div className="flex-center">
                    <StarFilled style={{ fontSize: "20px" }} />
                  </div>
                </Tooltip>
              )}

              {data.socialAnnouncement && (
                <Tooltip title="Social Announcement">
                  <div className="flex-center">
                    <img
                      src={AnnouncementIcon}
                      alt="social announcement"
                      style={{ width: 30, height: 30 }}
                    />
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
        );
      },
    },
    {
      width: 100,
      title: "Owner",
      key: "owner",
      dataIndex: "owner",
    },
    {
      width: 120,
      title: "Stage",
      key: "stage",
      dataIndex: "status",
    },
    {
      width: 150,
      title: "Next",
      key: "nextOutreachDate",
      dataIndex: "nextOutreachDate",
      render: (nextOutreachDate) => {
        return nextOutreachDate
          ? dayjs(nextOutreachDate).format("MMM DD, YYYY")
          : "-";
      },
      sorter: (a, b) => sortObjectByNumber(a, b, "nextOutreachDate"),
    },
    {
      width: 100,
      title: "Adoption",
      key: "adoption",
      dataIndex: "adoption",
      render: (adoption) => {
        return adoption ? `${adoption?.toFixed(0)}%` : "0%";
      },
      sorter: (a, b) => sortObjectByNumber(a, b, "adoption"),
    },
    {
      title: "% Reg",
      width: window.screen.width > 500 ? 100 : 50,
      key: "percentageRegistered",
      render: (data) => {
        let rm = data.registeredMembers || 0;
        let tm = data.totalMembers || 0;
        let val = (rm / tm) * 100;
        return (Number.isNaN(val) ? 0 : Math.round(val)) + "%";
      },
      dataIndex: "",
    },
    {
      title: `Reg`,
      width: window.screen.width > 500 ? 105 : 80,
      key: "registeredMembers",
      render: (registeredMembers) => registeredMembers || 0,
      dataIndex: "registeredMembers",
      sorter: (a, b) => sortObjectByNumber(a, b, "registeredMembers"),
    },
    {
      title: `Pending`,
      width: 120,
      key: "preRegisteredMemberCount",
      dataIndex: "preRegisteredMemberCount",
      render: (preRegisteredMemberCount) => preRegisteredMemberCount || 0,
      sorter: (a, b) => sortObjectByNumber(a, b, "preRegisteredMemberCount"),
    },
    {
      title: `Total`,
      width: 105,
      key: "totalMembers",
      dataIndex: "totalMembers",
      render: (total) => total || 0,
      sorter: (a, b) => sortObjectByNumber(a, b, "totalMembers"),
    },
    {
      title: `Channels`,
      width: 105,
      key: "showChatChannels",
      dataIndex: "showChatChannels",
      render: (showChatChannels) => (showChatChannels ? "Yes" : "No"),
    },
    {
      title: "Stripe?",
      key: "hasStripe",
      width: 100,
      render: (record) => (
        <span>
          {record.paymentDetails && record.paymentDetails.stripeAccountId
            ? "Yes"
            : "No"}
        </span>
      ),
    },
    {
      width: 100,
      title: "Website",
      dataIndex: "websitePublished",
      key: "website",
      render: (data) => <span>{data ? "Yes" : "No"}</span>,
    },
    {
      width: 100,
      title: "Directory",
      dataIndex: "directoryPublished",
      key: "directory",
      render: (data) => <span>{data ? "Yes" : "No"}</span>,
    },
    {
      width: 100,
      title: "Stadium",
      dataIndex: "stadiumPublished",
      key: "stadium",
      render: (data) => <span>{data ? "Yes" : "No"}</span>,
    },
    {
      width: 120,
      title: "Directory",
      key: "directoryLink",
      render: (_, record) => (
        <a
          href={`https://directory.chant.fan/details/${record.id}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#1677ff" }}
        >
          <LinkOutlined style={{ marginRight: 4 }} />
          Link
        </a>
      ),
    },
    {
      width: 120,
      title: "Website",
      key: "websiteLink",
      render: (_, record) => (
        <a
          href={`https://group.chant.fan/${record.groupName?.replace(/\s+/g, "").toLowerCase()}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#1677ff" }}
        >
          <LinkOutlined style={{ marginRight: 4 }} />
          Link
        </a>
      ),
    },
    {
      width: 150,
      title: "Global post",
      key: "clubPostAllowed",
      dataIndex: "clubPostAllowed",
      render: (value) => <span>{value ? "Yes" : "No"}</span>,
    },
    {
      width: 200,
      title: "Club",
      key: "clubName",
      dataIndex: "clubName",
      sorter: (a, b) => sortObjectByString(a, b, "clubName"),
    },
    {
      width: 200,
      title: "League",
      key: "leagueName",
      dataIndex: "leagueName",
      sorter: (a, b) => sortObjectByString(a, b, "leagueName"),
    },
    {
      width: 150,
      title: "Website Invoiced",
      key: "websiteInvoiced",
      dataIndex: "websiteInvoiced",
      sorter: (a, b) => sortObjectByNumber(a, b, "websiteInvoiced"),
      render: (websiteInvoiced) => {
        return getDateFromTimestamp(websiteInvoiced);
      },
    },
    {
      width: 150,
      title: "Renewal Date",
      key: "renewalDate",
      dataIndex: "renewalDate",
      sorter: (a, b) => sortObjectByNumber(a, b, "renewalDate"),
      render: (renewalDate) => {
        return getDateFromTimestamp(renewalDate);
      },
    },
    {
      width: 200,
      title: "Admins",
      key: "admins",
      dataIndex: "admins",
      render: (admins, record) => {
        return (
          <ul style={{ listStyleType: "none", paddingLeft: 0 }}>
            {admins &&
              Array.isArray(admins) &&
              [...(admins?.[0] || []), ...(admins?.[1] || [])]?.map(
                (admin, index) => {
                  return (
                    <li key={`${record.id}-${index}`}>
                      <Popover placement="topLeft" content="Copy Email">
                        <div
                          id={record.id}
                          onClick={() => copyToClipboard(admin.email)}
                          style={{
                            cursor: "pointer",
                            position: "relative",
                            color: "#486ddb",
                          }}
                        >
                          {admin.name}
                          <textarea id="email" style={{ display: "none" }}>
                            {admin.email}
                          </textarea>
                        </div>
                      </Popover>
                    </li>
                  );
                },
              )}
          </ul>
        );
      },
    },

    {
      width: 200,
      title: "Group Type",
      key: "groupType",
      dataIndex: "groupType",
      render: (type) => <span style={{ fontWeight: "bold" }}>{type}</span>,
      sorter: (a, b) => sortObjectByString(a, b, "groupType"),
    },
  ];

  return {
    clubs,
    leagues,
    countries,
    isLoading,
    columns,
    membersCount,
    filters,
    handleDropdownChange,
    handleSearchInputChange,
    handleDatePickerChange,
    handleRefresh,
    openNotesModal,
    selectedGroupDetails,
    filteredGroupsList,
    setOpenNotesModal,
    setSelectedGroupDetails,
  };
};

export default useGroups;
